import { useEffect, useState } from "react";
import { AlertTriangle, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Sticky banner shown at the top of the Emergency modal until the user
 * acknowledges it. Persists via `disclaimer_emergency_ack_at`.
 */
export default function EmergencyDisclaimer() {
  const { user, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hide immediately if already acked on server
  useEffect(() => { setDismissed(!!user?.disclaimer_emergency_ack_at); }, [user?.disclaimer_emergency_ack_at]);

  if (dismissed) return null;

  const acknowledge = async () => {
    setSaving(true);
    try {
      await api.post("/auth/acknowledge", { scope: "emergency" });
      setDismissed(true);
      refresh?.();
    } catch { setDismissed(true); }
    finally { setSaving(false); }
  };

  return (
    <div
      className="mx-4 mt-3 rounded-2xl border border-[#E63946]/40 bg-[#FFF5F5] p-3 md:p-4"
      data-testid="emergency-disclaimer"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E63946] font-bold mb-1">Emergency assistant — not a substitute</p>
          <p className="text-xs md:text-sm font-body text-kawaii-text leading-relaxed">
            This feature is an <strong>assistant only</strong>. It should <strong>not</strong> be solely relied upon in a real medical emergency.
            We accept no liability for outcomes arising from its use.
            <strong> In a life-threatening emergency, call your local emergency services first.</strong>
          </p>
          <button
            onClick={acknowledge}
            disabled={saving}
            data-testid="ack-emergency-btn"
            className="kawaii-btn mt-3 px-3 py-1.5 rounded-full bg-[#E63946] text-white text-[11px] font-body font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            <Check className="w-3 h-3" strokeWidth={2.5} />
            I understand — don't show again
          </button>
        </div>
      </div>
    </div>
  );
}
